'use no memo';
import { toast } from '@kalos/ui';
import { useStore } from 'zustand';

import {
  type DistributionType,
  type ModifiedTransactionLineItem,
} from '../../../../context/TransactionLIneItems';
import { useTransactionLineItemsStore } from '../../../../context/TransactionLIneItems/TransactionLInteItemsStoreContext';
import { useTransactionLineItemBatchMutation } from '../../../../hooks/react-query/useTransactionLineItem';
import { isTruthy } from '../../../../tools/typeguargs';
import {
  getLineItemUpdateFieldMask,
  wasTransactionLineItemUpdated,
} from './TransactionLineItems.utils';

/**
 * Asks before going ahead. Desktop uses the browser's confirm; phones pass their confirmation
 * sheet. Gets desktop's message, so the default reads exactly as before.
 */
export type ConfirmLineItemsChange = (message: string) => boolean | Promise<boolean>;

const browserConfirm: ConfirmLineItemsChange = (message) => window.confirm(message);

/** Drops unsaved line item edits (asking first when there are any) and leaves editing. */
export const useCancelLineItemEdits = ({
  confirm = browserConfirm,
}: { confirm?: ConfirmLineItemsChange } = {}) => {
  const store = useTransactionLineItemsStore();
  const setLineItems = useStore(store, (s) => s.setLineItems);
  const originalLineItems = useStore(store, (s) => s.transaction.transactionLineItems);
  const lineItems = useStore(store, (s) => s.lineItems);
  const setEditing = useStore(store, (s) => s.setIsEditing);

  return async () => {
    if (
      lineItems.some((li) =>
        wasTransactionLineItemUpdated({ original: originalLineItems, updated: li }),
      ) &&
      !(await confirm('Are you sure you want to cancel? You have unsaved changes!'))
    ) {
      return;
    }

    setEditing(false);
    setLineItems(originalLineItems);
  };
};

/** Saves the line items in one batch: creates, deletes and updates, after desktop's checks. */
export const useSaveLineItems = (
  onSave: () => void,
  { confirm = browserConfirm }: { confirm?: ConfirmLineItemsChange } = {},
) => {
  const store = useTransactionLineItemsStore();
  const setEditing = useStore(store, (s) => s.setIsEditing);
  const lineItems = useStore(store, (s) => s.lineItems);
  const originalLineItems = useStore(store, (s) => s.transaction.transactionLineItems);

  const isDisabled = useStore(store, (s) => s.shouldBlockSave());

  const batchUpdateMutation = useTransactionLineItemBatchMutation();

  const save = async () => {
    if (lineItems.length > 0 && lineItems.every((li) => li.description.length > 255)) {
      toast({
        variant: 'destructive',
        title: 'Description cannot be more than 255 characters!',
      });
      return;
    }

    if (!lineItems.every((li) => li.amount !== 0)) {
      toast({
        variant: 'destructive',
        title: 'Amount cannot be 0!',
      });
      return;
    }

    if (lineItems.length > 0 && lineItems.every((li) => li.secondaryAmountNote.length > 255)) {
      toast({
        variant: 'destructive',
        title: 'Additional Amount Notes cannot be more than 255 characters!',
      });
      return;
    }

    if (
      !store.getState().isValidForSave() &&
      !(await confirm(
        "Are you sure you want to save line items in an invalid state? Transaction with invalid line items won't be applicable for submission!",
      ))
    ) {
      return;
    }

    const newLineItems = lineItems
      .filter((li) => li.new)
      .map((li) => {
        li.id = 0;
        // to reset given random id and prevent issues during creation
        return li;
      });
    const deletedLineItems = originalLineItems.filter(
      (oli) => !lineItems.find((li) => li.id === oli.id),
    );
    const updatedLineItems = lineItems
      .filter((li) => !li.new && !deletedLineItems.find((dli) => dli.id === li.id))
      .map((li) => {
        const originalLineItem = originalLineItems.find((oli) => oli.id === li.id);
        if (!originalLineItem) {
          throw new Error('Original line item not found');
        }
        const fieldMask = getLineItemUpdateFieldMask({
          original: originalLineItem,
          updated: li,
        });

        if (!fieldMask.length) {
          return null;
        }
        li.fieldMask = fieldMask;
        return li;
      })
      .filter(isTruthy);

    // if there are any updates
    if (!(!newLineItems.length && !deletedLineItems.length && !updatedLineItems.length)) {
      try {
        await batchUpdateMutation.mutateAsync({
          lineItemsToCreate: newLineItems,
          lineItemsToDelete: deletedLineItems,
          lineItemsToUpdate: updatedLineItems,
        });
        onSave();
      } catch {
        toast({
          variant: 'destructive',
          title: 'Failed to update line items',
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Successfully updated line items!',
      });
    }

    setEditing(false);
  };

  return { save, isSaving: batchUpdateMutation.isPending, isDisabled };
};

/**
 * What adding a distribution amount puts on each line item: the whole amount on every one when
 * even, else each one's share of the amount by its part of the receipt total.
 */
export const getDistributionAmounts = ({
  lineItems,
  amount,
  distributionType,
  totalAmount,
}: {
  lineItems: ModifiedTransactionLineItem[];
  amount: number;
  distributionType: DistributionType;
  totalAmount: number;
}) => {
  const amountToApplyList = lineItems.map((li) => ({ amount: amount, lineItem: li }));

  if (distributionType !== 'even') {
    amountToApplyList.forEach((item) => {
      const percentage = item.lineItem.amount / totalAmount;
      item.amount = parseFloat((amount * percentage).toFixed(2));
    });
  }

  return amountToApplyList;
};
